import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n, EVENT_TYPES, DOWNLOAD_MODES, SALES_MODES } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";

const field = "min-h-[46px] w-full border border-[#C69A3B]/60 bg-white px-3 text-sm";
const STEPS = [
  { es: "Detalles del Evento", en: "Event Details" },
  { es: "Imagen de Portada", en: "Cover Image" },
  { es: "Privacidad", en: "Privacy" },
  { es: "Descargas", en: "Downloads" },
  { es: "Marca de Agua", en: "Watermark" },
  { es: "Ventas", en: "Sales" },
  { es: "Álbumes", en: "Albums" },
  { es: "Publicar", en: "Publish" },
];

export default function CreateEventWizard({ onCreated }) {
  const { t, lang } = useI18n();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [form, setForm] = useState({
    name_es: "", name_en: "", description_es: "", description_en: "",
    event_date: "", location: "", event_type: "reunion", cover_image: "",
    privacy: "public", password: "", invited_emails: "",
    download_mode: "individual", watermark: false, sales_mode: "disabled",
    featured: false, contributions_enabled: false, sharing_enabled: true,
    fallback_language: "es",
    albums: [{ name_es: "", name_en: "" }],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (published) => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        invited_emails: form.invited_emails.split(",").map((s) => s.trim()).filter(Boolean),
        albums: form.albums.filter((a) => a.name_es.trim()),
        published,
        cover_image: form.cover_image || null,
        password: form.privacy === "password" ? form.password : null,
      };
      const { data } = await api.post("/admin/events", payload);
      toast.success(data.slug);
      onCreated?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl border border-[#C69A3B]/40 bg-white p-6 lg:p-8" data-testid="create-event-wizard">
      <ol className="mb-8 flex flex-wrap gap-2 text-xs">
        {STEPS.map((s, i) => (
          <li
            key={i}
            data-testid={`wizard-step-indicator-${i + 1}`}
            className={`border px-3 py-1 ${
              i === step ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B]/40 text-[#5A5752]"
            }`}
          >
            {i + 1}. {s[lang]}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="wizard-step-1">
          <label className="block">
            <span className="label-mono">Nombre — Español *</span>
            <input className={field} data-testid="wizard-name-es" value={form.name_es} onChange={(e) => set("name_es", e.target.value)} />
          </label>
          <label className="block">
            <span className="label-mono">Name — English</span>
            <input className={field} data-testid="wizard-name-en" value={form.name_en} onChange={(e) => set("name_en", e.target.value)} />
          </label>
          <label className="block">
            <span className="label-mono">{t("common.date")} *</span>
            <input type="date" className={field} data-testid="wizard-date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} />
          </label>
          <label className="block">
            <span className="label-mono">{t("common.location")}</span>
            <input className={field} data-testid="wizard-location" value={form.location} onChange={(e) => set("location", e.target.value)} />
          </label>
          <label className="block">
            <span className="label-mono">{t("common.type")}</span>
            <select className={field} data-testid="wizard-type" value={form.event_type} onChange={(e) => set("event_type", e.target.value)}>
              {Object.keys(EVENT_TYPES).map((k) => (
                <option key={k} value={k}>{EVENT_TYPES[k][lang]}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label-mono">Idioma de respaldo</span>
            <select className={field} data-testid="wizard-fallback" value={form.fallback_language} onChange={(e) => set("fallback_language", e.target.value)}>
              <option value="es">Mostrar español si falta traducción</option>
              <option value="hide">Ocultar texto sin traducir</option>
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="label-mono">Descripción — Español</span>
            <textarea rows={3} className="w-full border border-[#C69A3B]/60 p-3 text-sm" data-testid="wizard-desc-es" value={form.description_es} onChange={(e) => set("description_es", e.target.value)} />
          </label>
          <label className="block sm:col-span-2">
            <span className="label-mono">Description — English</span>
            <textarea rows={3} className="w-full border border-[#C69A3B]/60 p-3 text-sm" data-testid="wizard-desc-en" value={form.description_en} onChange={(e) => set("description_en", e.target.value)} />
          </label>
        </div>
      )}

      {step === 1 && (
        <div data-testid="wizard-step-2">
          <span className="label-mono">Imagen de portada</span>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept="image/*"
              data-testid="wizard-cover-file"
              disabled={coverBusy}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                setCoverBusy(true);
                try {
                  const body = new FormData();
                  body.append("file", file);
                  const { data } = await api.post("/admin/site-images", body);
                  set("cover_image", data.url);
                  toast.success(t("admin.coverSet"));
                } catch (err) {
                  toast.error(apiError(err.response?.data?.detail));
                } finally {
                  setCoverBusy(false);
                }
              }}
              className="text-sm"
            />
          </div>
          <label className="mt-4 block">
            <span className="label-mono">o pegue una dirección (URL)</span>
            <input className={field} data-testid="wizard-cover" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="https://…" />
          </label>
          {form.cover_image && (
            <img src={form.cover_image} alt="cover" data-testid="wizard-cover-preview" className="mt-4 aspect-[3/2] w-64 border border-[#C69A3B]/40 object-cover" />
          )}
          <p className="mt-3 text-xs text-[#5A5752]">
            También puede cambiarla luego desde Eventos, haciendo clic en la miniatura del evento.
          </p>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="wizard-step-3">
          {["public", "password", "private"].map((p) => (
            <label key={p} className="flex items-center gap-3 border border-[#C69A3B]/40 p-4 text-sm">
              <input type="radio" name="privacy" data-testid={`wizard-privacy-${p}`} checked={form.privacy === p} onChange={() => set("privacy", p)} />
              {t(`privacy.${p}`)}
            </label>
          ))}
          {form.privacy === "password" && (
            <label className="block">
              <span className="label-mono">{t("privacy.password.label")}</span>
              <input className={field} data-testid="wizard-password" value={form.password} onChange={(e) => set("password", e.target.value)} />
            </label>
          )}
          {form.privacy === "private" && (
            <label className="block">
              <span className="label-mono">Correos invitados (separados por coma)</span>
              <input className={field} data-testid="wizard-invited" value={form.invited_emails} onChange={(e) => set("invited_emails", e.target.value)} />
            </label>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid="wizard-step-4">
          {Object.keys(DOWNLOAD_MODES).map((m) => (
            <label key={m} className="flex items-center gap-3 border border-[#C69A3B]/40 p-4 text-sm">
              <input type="radio" name="dl" data-testid={`wizard-download-${m}`} checked={form.download_mode === m} onChange={() => set("download_mode", m)} />
              {DOWNLOAD_MODES[m][lang]}
            </label>
          ))}
        </div>
      )}

      {step === 4 && (
        <label className="flex items-center gap-3 border border-[#C69A3B]/40 p-4 text-sm" data-testid="wizard-step-5">
          <input type="checkbox" data-testid="wizard-watermark" checked={form.watermark} onChange={(e) => set("watermark", e.target.checked)} />
          {t("common.watermark")} — HR Creations
        </label>
      )}

      {step === 5 && (
        <div className="space-y-3" data-testid="wizard-step-6">
          {Object.keys(SALES_MODES).map((m) => (
            <label key={m} className="flex items-center gap-3 border border-[#C69A3B]/40 p-4 text-sm">
              <input type="radio" name="sales" data-testid={`wizard-sales-${m}`} checked={form.sales_mode === m} onChange={() => set("sales_mode", m)} />
              {SALES_MODES[m][lang]}
            </label>
          ))}
        </div>
      )}

      {step === 6 && (
        <div className="space-y-3" data-testid="wizard-step-7">
          {form.albums.map((a, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-2">
              <input
                className={field}
                placeholder="Álbum — Español"
                data-testid={`wizard-album-es-${i}`}
                value={a.name_es}
                onChange={(e) =>
                  set("albums", form.albums.map((x, idx) => (idx === i ? { ...x, name_es: e.target.value } : x)))
                }
              />
              <input
                className={field}
                placeholder="Album — English"
                data-testid={`wizard-album-en-${i}`}
                value={a.name_en}
                onChange={(e) =>
                  set("albums", form.albums.map((x, idx) => (idx === i ? { ...x, name_en: e.target.value } : x)))
                }
              />
            </div>
          ))}
          <GoldButton
            variant="outline"
            data-testid="wizard-add-album"
            onClick={() => set("albums", [...form.albums, { name_es: "", name_en: "" }])}
          >
            + Álbum
          </GoldButton>
        </div>
      )}

      {step === 7 && (
        <div className="space-y-3 text-sm" data-testid="wizard-step-8">
          <p className="label-mono">Vista previa</p>
          <p className="font-serif-display text-2xl text-[#8F3329]">{form.name_es || "—"}</p>
          <ul className="space-y-1 text-[#5A5752]">
            <li>{t("common.date")}: {form.event_date || "—"}</li>
            <li>{t("common.location")}: {form.location || "—"}</li>
            <li>{t("common.privacy")}: {t(`privacy.${form.privacy}`)}</li>
            <li>{t("common.downloads")}: {DOWNLOAD_MODES[form.download_mode][lang]}</li>
            <li>{t("common.watermark")}: {form.watermark ? t("common.yes") : t("common.no")}</li>
            <li>Ventas: {SALES_MODES[form.sales_mode][lang]}</li>
            <li>{t("events.albums")}: {form.albums.filter((a) => a.name_es).length}</li>
          </ul>
          <label className="flex items-center gap-2">
            <input type="checkbox" data-testid="wizard-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
            Destacado en la portada
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" data-testid="wizard-contributions" checked={form.contributions_enabled} onChange={(e) => set("contributions_enabled", e.target.checked)} />
            Permitir contribuciones de invitados
          </label>
          <div className="flex flex-wrap gap-3 pt-3">
            <GoldButton disabled={busy || !form.name_es || !form.event_date} onClick={() => submit(true)} data-testid="wizard-publish-btn">
              {t("btn.publish")}
            </GoldButton>
            <GoldButton variant="outline" disabled={busy || !form.name_es || !form.event_date} onClick={() => submit(false)} data-testid="wizard-draft-btn">
              {t("common.draft")}
            </GoldButton>
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-between border-t border-[#C69A3B]/30 pt-5">
        <GoldButton variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="wizard-prev-btn">
          ← {t("btn.prev")}
        </GoldButton>
        <GoldButton variant="outline" disabled={step === STEPS.length - 1} onClick={() => setStep((s) => s + 1)} data-testid="wizard-next-btn">
          {t("btn.next")} →
        </GoldButton>
      </div>
    </div>
  );
}
